import { useState, useEffect, useRef, useCallback } from "react";
import { OrderType } from "../SpecificTradePanel";
import {
  registerOrderLineOverlay,
  createOrderLineOverlay,
  OrderLineData,
} from "@/lib/chart-helpers/custom-overlays/specific-trade-panel/orderLineOverlay";
import {
  registerOrderSlTpOverlay,
  createOrderSlTpOverlay,
  OrderSlTpData,
} from "@/lib/chart-helpers/custom-overlays/specific-trade-panel/orderSlTpOverlay";

interface UseOrderLinesNewProps {
  chartInstanceRef: React.RefObject<any>;
  currentQuote: any;
  isVisible: boolean;
  slEnabled?: boolean;
  tpEnabled?: boolean;
  onPriceChange?: (orderType: OrderType, price: number) => void;
  onSLPriceChange?: (price: number) => void;
  onTPPriceChange?: (price: number) => void;
}

interface UseOrderLinesNewReturn {
  orderLinePrice: number;
  currentOrderType: OrderType;
  slPrice: number;
  tpPrice: number;
  setCurrentOrderType: (orderType: OrderType) => void;
  updateOrderLinePrice: (price: number) => void;
  updateSLLinePrice: (price: number) => void;
  updateTPLinePrice: (price: number) => void;
}

export const useOrderLinesNew = ({
  chartInstanceRef,
  currentQuote,
  isVisible,
  slEnabled = false,
  tpEnabled = false,
  onPriceChange,
  onSLPriceChange,
  onTPPriceChange,
}: UseOrderLinesNewProps): UseOrderLinesNewReturn => {
  const [currentOrderType, setCurrentOrderType] = useState<OrderType>("Buy Limit");
  const [orderLinePrice, setOrderLinePrice] = useState<number>(0);
  const [slPrice, setSlPrice] = useState<number>(0);
  const [tpPrice, setTpPrice] = useState<number>(0);

  const [orderLineId, setOrderLineId] = useState<string | null>(null);
  const [slLineId, setSlLineId] = useState<string | null>(null);
  const [tpLineId, setTpLineId] = useState<string | null>(null);

  const orderLineIdRef = useRef<string | null>(null);
  const slLineIdRef = useRef<string | null>(null);
  const tpLineIdRef = useRef<string | null>(null);

  const currentOrderTypeRef = useRef(currentOrderType);
  const isVisibleRef = useRef(isVisible);

  useEffect(() => {
    registerOrderLineOverlay();
    registerOrderSlTpOverlay();
  }, []);

  useEffect(() => {
    currentOrderTypeRef.current = currentOrderType;
  }, [currentOrderType]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  /** Removes chart overlays using latest ids from refs (safe on unmount). */
  const stripPlacementOverlaysFromChart = useCallback(() => {
    const chart = chartInstanceRef.current;
    const ids = [
      orderLineIdRef.current,
      slLineIdRef.current,
      tpLineIdRef.current,
    ];
    if (chart) {
      for (const id of ids) {
        if (!id) continue;
        try {
          chart.removeOverlay(id);
        } catch (error) {
          console.error("Failed to remove placement overlay:", error);
        }
      }
    }
    orderLineIdRef.current = null;
    slLineIdRef.current = null;
    tpLineIdRef.current = null;
  }, [chartInstanceRef]);

  const resetPlacementOverlayState = useCallback(() => {
    stripPlacementOverlaysFromChart();
    setOrderLineId(null);
    setSlLineId(null);
    setTpLineId(null);
    setSlPrice(0);
    setTpPrice(0);
    setOrderLinePrice(0);
  }, [stripPlacementOverlaysFromChart]);

  const updateOverlayPrice = (overlayId: string | null, price: number) => {
    if (!chartInstanceRef.current || !overlayId || price <= 0) return;

    const overlay = chartInstanceRef.current.getOverlayById?.(overlayId);
    if (!overlay) return;

    const nextExtendData = {
      ...overlay.extendData,
      price,
    };

    chartInstanceRef.current.overrideOverlay({
      id: overlayId,
      points: [
        {
          ...(overlay.points?.[0] || {}),
          value: price,
        },
      ],
      extendData: nextExtendData,
    });

    const updatedOverlay = chartInstanceRef.current.getOverlayById?.(overlayId);
    if (updatedOverlay && overlay.onPriceChange) {
      updatedOverlay.onPriceChange = overlay.onPriceChange;
    }
  };

  const updateOrderLinePrice = (price: number) => {
    setOrderLinePrice(price);
    updateOverlayPrice(orderLineIdRef.current, price);
  };

  const updateSLLinePrice = (price: number) => {
    setSlPrice(price);
    updateOverlayPrice(slLineIdRef.current, price);
  };

  const updateTPLinePrice = (price: number) => {
    setTpPrice(price);
    updateOverlayPrice(tpLineIdRef.current, price);
  };

  const createOrderLine = (orderType: OrderType, price?: number) => {
    if (!chartInstanceRef.current || !currentQuote) return;

    const currentPrice = (currentQuote.bid + currentQuote.ask) / 2;
    const linePrice = price || orderLinePrice || currentPrice;

    const existingId = orderLineIdRef.current;
    if (existingId && chartInstanceRef.current) {
      try {
        chartInstanceRef.current.removeOverlay(existingId);
      } catch (error) {
        console.error("Failed to remove existing order line:", error);
      }
      orderLineIdRef.current = null;
    }

    const orderData: OrderLineData = {
      price: linePrice,
      orderType: orderType,
      lots: 1.0,
    };

    try {
      const newOverlayId = createOrderLineOverlay(
        chartInstanceRef.current,
        orderData,
        (type: string, newPrice: number) => {
          setOrderLinePrice(newPrice);
          if (onPriceChange) {
            onPriceChange(type as OrderType, newPrice);
          }
        }
      );

      if (newOverlayId) {
        orderLineIdRef.current = newOverlayId;
        setOrderLineId(newOverlayId);
        setOrderLinePrice(linePrice);
      }
    } catch (error) {
      console.error("Failed to create order line:", error);
    }
  };

  const createSLLine = (price?: number) => {
    if (!chartInstanceRef.current || !currentQuote) return;

    const currentPrice = (currentQuote.bid + currentQuote.ask) / 2;
    const linePrice = price || currentPrice * 0.9995;

    const existingId = slLineIdRef.current;
    if (existingId && chartInstanceRef.current) {
      try {
        chartInstanceRef.current.removeOverlay(existingId);
      } catch (error) {
        console.error("Failed to remove existing SL line:", error);
      }
      slLineIdRef.current = null;
    }

    const slData: OrderSlTpData = {
      price: linePrice,
      type: "SL",
    };

    try {
      const newSlId = createOrderSlTpOverlay(
        chartInstanceRef.current,
        slData,
        (_type: string, newPrice: number) => {
          setSlPrice(newPrice);
          if (onSLPriceChange) {
            onSLPriceChange(newPrice);
          }
        }
      );

      if (newSlId) {
        slLineIdRef.current = newSlId;
        setSlLineId(newSlId);
        setSlPrice(linePrice);
      }
    } catch (error) {
      console.error("Failed to create SL line:", error);
    }
  };

  const createTPLine = (price?: number) => {
    if (!chartInstanceRef.current || !currentQuote) return;

    const currentPrice = (currentQuote.bid + currentQuote.ask) / 2;
    const linePrice = price || currentPrice * 1.0005;

    const existingId = tpLineIdRef.current;
    if (existingId && chartInstanceRef.current) {
      try {
        chartInstanceRef.current.removeOverlay(existingId);
      } catch (error) {
        console.error("Failed to remove existing TP line:", error);
      }
      tpLineIdRef.current = null;
    }

    const tpData: OrderSlTpData = {
      price: linePrice,
      type: "TP",
    };

    try {
      const newTpId = createOrderSlTpOverlay(
        chartInstanceRef.current,
        tpData,
        (_type: string, newPrice: number) => {
          setTpPrice(newPrice);
          if (onTPPriceChange) {
            onTPPriceChange(newPrice);
          }
        }
      );

      if (newTpId) {
        tpLineIdRef.current = newTpId;
        setTpLineId(newTpId);
        setTpPrice(linePrice);
      }
    } catch (error) {
      console.error("Failed to create TP line:", error);
    }
  };

  useEffect(() => {
    if (isVisible && chartInstanceRef.current && currentQuote && !orderLineId) {
      createOrderLine(currentOrderType);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && orderLineId && chartInstanceRef.current && currentQuote) {
      createOrderLine(
        currentOrderType,
        orderLinePrice > 0 ? orderLinePrice : undefined
      );
    }
  }, [currentOrderType]);

  useEffect(() => {
    if (slEnabled && isVisible && chartInstanceRef.current && currentQuote && !slLineId) {
      createSLLine();
    } else if (!slEnabled && slLineId && chartInstanceRef.current) {
      try {
        chartInstanceRef.current.removeOverlay(slLineId);
        slLineIdRef.current = null;
        setSlLineId(null);
        setSlPrice(0);
      } catch (error) {
        console.error("Failed to remove SL line:", error);
      }
    }
  }, [slEnabled, isVisible]);

  useEffect(() => {
    if (tpEnabled && isVisible && chartInstanceRef.current && currentQuote && !tpLineId) {
      createTPLine();
    } else if (!tpEnabled && tpLineId && chartInstanceRef.current) {
      try {
        chartInstanceRef.current.removeOverlay(tpLineId);
        tpLineIdRef.current = null;
        setTpLineId(null);
        setTpPrice(0);
      } catch (error) {
        console.error("Failed to remove TP line:", error);
      }
    }
  }, [tpEnabled, isVisible]);

  useEffect(() => {
    if (!isVisible && chartInstanceRef.current) {
      resetPlacementOverlayState();
    }
  }, [isVisible, resetPlacementOverlayState]);

  useEffect(() => {
    return () => {
      stripPlacementOverlaysFromChart();
    };
  }, [stripPlacementOverlaysFromChart]);

  return {
    orderLinePrice,
    currentOrderType,
    slPrice,
    tpPrice,
    setCurrentOrderType,
    updateOrderLinePrice,
    updateSLLinePrice,
    updateTPLinePrice,
  };
};
